import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import RoleBasedRouter from './components/RoleBasedRouter';

import { BottomNavigation } from './components/BottomNavigation';
import { CartProvider } from './contexts/CartContext';
import { QuickActionsProvider } from './contexts/QuickActionsContext';
import Cart from './pages/Cart';

// Pages
import Landing from './pages/Landing';
import SignIn from './pages/SignIn';
import SignUp from './pages/SignUp';
import ForgotPassword from './pages/ForgotPassword';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import TermsServiceUserAgreement from './pages/legal/TermsServiceUserAgreement';
import CustomerDashboard from './pages/CustomerDashboard';
import SellerHub from './pages/SellerHub';
import AdminDashboard from './pages/AdminDashboard';
import { BuyerCancellationDashboard } from './components/BuyerCancellationDashboard';
import { AdminCancellationDashboard } from './components/AdminCancellationDashboard';
import Profile from './pages/Profile';
import FoodDiscovery from './pages/FoodDiscovery';
import OnboardingFlow from './pages/OnboardingFlow';
import VerifyIdentityStep from './pages/onboarding/VerifyIdentityStep';
import VerifyIdentityReturn from './pages/onboarding/VerifyIdentityReturn';
import OnboardingSuccess from './pages/onboarding/OnboardingSuccess';
import { FoodBrowsing } from './pages/FoodBrowsing';
import { FoodDetail } from './pages/FoodDetail';
import Storefront from './pages/Storefront';
import StorePreview from './pages/StorePreview';
import StoreProductDetail from './pages/StoreProductDetail';
import { ProductReviewViewer } from './pages/ProductReviewViewer';
import { AddProduct } from './pages/AddProduct';
import AllProducts from './pages/AllProducts';
import { AdminTools } from './pages/AdminTools';
import { AdminTaxonomy } from './pages/AdminTaxonomy';
import AdminArchivedProducts from './pages/AdminArchivedProducts';
import AdminMigration from './pages/AdminMigration';
import Checkout from './pages/Checkout';
import OrderConfirmation from './pages/OrderConfirmation';
import OrderHistory from './pages/OrderHistory';
import { CustomerMessages } from './pages/CustomerMessages';
import OrderDetails from './pages/OrderDetails';
import { Orders } from './pages/Orders';
import { OrderDetail } from './pages/OrderDetail';
import Favorites from './pages/Favorites';
import CreateProductVideo from './pages/CreateProductVideo';
import LegalCenter from './pages/LegalCenter';
import Documents from './pages/Documents';
import BankAccountConnection from './pages/BankAccountConnection';
import { StoreSettings } from './pages/StoreSettings';
import SellerFinancials from './pages/SellerFinancials';
import { SellerConversions } from './pages/SellerConversions';
import { StoreReviews } from './pages/StoreReviews';
import { SellerMessages } from './pages/SellerMessages';
import SellerNotifications from './pages/SellerNotifications';
import Discounts from './pages/Discounts';
import QRCodePage from './pages/QRCodePage';
import RefundsCancellationsDisputes from './pages/legal/RefundsCancellationsDisputes';
import DeliveryPickupPolicy from './pages/legal/DeliveryPickupPolicy';
import CommunityContentPolicy from './pages/legal/CommunityContentPolicy';
import BuyerPolicy from './pages/legal/BuyerPolicy';
import SellerPolicy from './pages/legal/SellerPolicy';
import BuyerPolicies from './pages/legal/BuyerPolicies';
import SellerPolicies from './pages/legal/SellerPolicies';
import FoodSafetyCompliance from './pages/legal/FoodSafetyCompliance';
import PaymentFeePolicy from './pages/legal/PaymentFeePolicy';
import PreorderCustomPolicy from './pages/legal/PreorderCustomPolicy';
import CateringInhomeChefPolicy from './pages/legal/CateringInhomeChefPolicy';
import ProhibitedItemsActivities from './pages/legal/ProhibitedItemsActivities';
import ContentCommunityGuidelines from './pages/legal/ContentCommunityGuidelines';
import IntellectualPropertyDMCA from './pages/legal/IntellectualPropertyDMCA';

// Placeholder components for missing pages
const Conversations = () => (
  <div style={{ 
    minHeight: '100vh', 
    background: '#F9F9F9', 
    display: 'flex', 
    alignItems: 'center', 
    justifyContent: 'center',
    paddingBottom: '120px',
    fontFamily: 'Montserrat, sans-serif'
  }}>
    <div style={{ textAlign: 'center' }}>
      <h1 style={{ fontSize: 32, color: '#222', marginBottom: 16 }}>Conversations</h1>
      <p style={{ color: '#757575', fontSize: 18 }}>Chat with food creators coming soon!</p>
    </div>
    <BottomNavigation />
  </div>
);

function App() {
  // Create a QueryClient instance
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 5 * 60 * 1000, // 5 minutes
        cacheTime: 10 * 60 * 1000, // 10 minutes
        retry: 3,
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
        refetchOnWindowFocus: false,
        refetchOnReconnect: true,
      },
      mutations: {
        retry: 1,
        retryDelay: 1000,
      },
    },
  });

  // Wrapper for standalone identity verification route
  const VerifyIdentityWrapper = () => {
    const navigate = useNavigate();
    
    const handleNext = () => {
      navigate('/onboarding/success');
    };
    
    const handleBack = () => {
      navigate('/onboarding');
    };
    
    return (
      <VerifyIdentityStep
        data={{}}
        onNext={handleNext}
        onBack={handleBack}
        isFirst={false}
        isLast={false}
      />
    );
  };

  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <AuthProvider>
          <CartProvider>
            <div className="App">
            <Routes>
              {/* Public routes */}
              <Route path="/landing" element={<Landing />} />
              <Route path="/signin" element={<SignIn />} />
              <Route path="/signup" element={<SignUp />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/legal" element={<LegalCenter />} />
              <Route path="/legal/buyer" element={<BuyerPolicies />} />
              <Route path="/legal/seller" element={<SellerPolicies />} />
              <Route path="/legal/terms" element={<Terms />} />
              <Route path="/legal/privacy" element={<Privacy />} />
              <Route path="/legal/terms-service-user-agreement" element={<TermsServiceUserAgreement />} />
              <Route path="/legal/refunds-cancellations-disputes" element={<RefundsCancellationsDisputes />} />
              <Route path="/legal/delivery-pickup" element={<DeliveryPickupPolicy />} />
              <Route path="/legal/community-content" element={<CommunityContentPolicy />} />
              <Route path="/legal/buyer-policy" element={<BuyerPolicy />} />
              <Route path="/legal/seller-policy" element={<SellerPolicy />} />
              <Route path="/legal/food-safety-compliance" element={<FoodSafetyCompliance />} />
              <Route path="/legal/payment-fee" element={<PaymentFeePolicy />} />
              <Route path="/legal/preorder-custom" element={<PreorderCustomPolicy />} />
              <Route path="/legal/catering-inhome-chef" element={<CateringInhomeChefPolicy />} />
              <Route path="/legal/prohibited-items" element={<ProhibitedItemsActivities />} />
              <Route path="/legal/content-community-guidelines" element={<ContentCommunityGuidelines />} />
              <Route path="/legal/intellectual-property-dmca" element={<IntellectualPropertyDMCA />} />
              
              {/* Protected routes with role-based access */}
              <Route path="/" element={<RoleBasedRouter />} />
              <Route 
                path="/customer-dashboard" 
                element={
                  <ProtectedRoute>
                    <CustomerDashboard />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/fooddiscovery" 
                element={
                  <ProtectedRoute requireAuth={false}>
                    <FoodDiscovery />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/food-browsing" 
                element={
                  <ProtectedRoute requireAuth={false}>
                    <FoodBrowsing />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/order-history" 
                element={
                  <ProtectedRoute>
                    <OrderHistory />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/customer-messages" 
                element={
                  <ProtectedRoute>
                    <CustomerMessages />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/customer-cancellations" 
                element={
                  <ProtectedRoute>
                    <BuyerCancellationDashboard />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/order-details/:orderId" 
                element={
                  <ProtectedRoute>
                    <OrderDetails />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/products/:productId"
                element={
                  <ProtectedRoute requireAuth={false}>
                    <StoreProductDetail />
                  </ProtectedRoute>
                }
              />
              <Route 
                path="/storefront/:sellerId" 
                element={<ProtectedRoute requireAuth={false}><Storefront /></ProtectedRoute>} 
              />
              <Route 
                path="/store-preview/:sellerId" 
                element={<ProtectedRoute requiredRole="creator"><StorePreview /></ProtectedRoute>} 
              />

              <Route 
                path="/storefront/:storeId/reviews" 
                element={<ProtectedRoute><StoreReviews /></ProtectedRoute>} 
              />
              <Route 
                path="/food/:foodId"
                element={
                  <ProtectedRoute requireAuth={false}>
                    <FoodDetail />
                  </ProtectedRoute>
                }
              />
              <Route 
                path="/food/:foodId/reviews" 
                element={
                  <ProtectedRoute>
                    <ProductReviewViewer />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/seller-hub" 
                element={
                  <ProtectedRoute requiredRole="creator">
                    <QuickActionsProvider>
                      <SellerHub />
                    </QuickActionsProvider>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/seller-hub/payments/bank" 
                element={
                  <ProtectedRoute requiredRole="creator">
                    <BankAccountConnection />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/seller-hub/financials" 
                element={
                  <ProtectedRoute requiredRole="creator">
                    <SellerFinancials />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/seller-hub/conversions" 
                element={
                  <ProtectedRoute requiredRole="creator">
                    <SellerConversions />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/seller-hub/messages" 
                element={
                  <ProtectedRoute requiredRole="creator">
                    <QuickActionsProvider>
                      <SellerMessages />
                    </QuickActionsProvider>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/seller-hub/notifications" 
                element={
                  <ProtectedRoute requiredRole="creator">
                    <QuickActionsProvider>
                      <SellerNotifications />
                    </QuickActionsProvider>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/seller-hub/qr-code" 
                element={
                  <ProtectedRoute requiredRole="creator">
                    <QuickActionsProvider>
                      <QRCodePage />
                    </QuickActionsProvider>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/seller-hub/store-settings/:storeId" 
                element={
                  <ProtectedRoute requiredRole="creator">
                    <QuickActionsProvider>
                      <StoreSettings />
                    </QuickActionsProvider>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/discounts" 
                element={
                  <ProtectedRoute requiredRole="creator">
                    <Discounts />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/store-settings/:storeId" 
                element={
                  <ProtectedRoute requiredRole="creator">
                    <QuickActionsProvider>
                      <StoreSettings />
                    </QuickActionsProvider>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/documents" 
                element={
                  <ProtectedRoute requiredRole="creator">
                    <Documents />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/admin-dashboard" 
                element={
                  <ProtectedRoute requiredRole="admin">
                    <AdminDashboard />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/admin-tools" 
                element={
                  <ProtectedRoute requiredRole="admin">
                    <AdminTools />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/admin/taxonomy" 
                element={
                  <ProtectedRoute requiredRole="admin">
                    <AdminTaxonomy />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/admin/archived-products" 
                element={
                  <ProtectedRoute requiredRole="admin">
                    <AdminArchivedProducts />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/admin/migration" 
                element={
                  <ProtectedRoute requiredRole="admin">
                    <AdminMigration />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/admin-cancellations" 
                element={
                  <ProtectedRoute requiredRole="admin">
                    <AdminCancellationDashboard />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/profile" 
                element={
                  <ProtectedRoute>
                    <Profile />
                  </ProtectedRoute>
                } 
              />
              <Route path="/onboarding" element={<OnboardingFlow />} />
              <Route 
                path="/onboarding/verify-identity" 
                element={
                  <ProtectedRoute>
                    <VerifyIdentityWrapper />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/onboarding/verify-identity/return" 
                element={
                  <ProtectedRoute>
                    <VerifyIdentityReturn />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/onboarding/success" 
                element={
                  <ProtectedRoute>
                    <OnboardingSuccess />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/conversations" 
                element={
                  <ProtectedRoute>
                    <Conversations />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/cart" 
                element={
                  <ProtectedRoute requireAuth={false}>
                    <Cart />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/add-product" 
                element={
                  <ProtectedRoute requiredRole="creator">
                    <AddProduct />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/create-product-video" 
                element={
                  <ProtectedRoute requiredRole="creator">
                    <CreateProductVideo />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/all-products" 
                element={
                  <ProtectedRoute requiredRole="creator">
                    <QuickActionsProvider>
                      <AllProducts />
                    </QuickActionsProvider>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/checkout" 
                element={
                  <ProtectedRoute>
                    <Checkout />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/order-confirmation/:orderId" 
                element={
                  <ProtectedRoute>
                    <OrderConfirmation />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/orders" 
                element={
                  <ProtectedRoute requiredRole="creator">
                    <QuickActionsProvider>
                      <Orders />
                    </QuickActionsProvider>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/orders/:orderId" 
                element={
                  <ProtectedRoute requiredRole="creator">
                    <OrderDetails />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/favorites" 
                element={
                  <ProtectedRoute>
                    <Favorites />
                  </ProtectedRoute>
                } 
              />
              
              {/* Catch all route */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </div>
        </CartProvider>

      </AuthProvider>
      <ReactQueryDevtools initialIsOpen={false} />
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
            fontSize: '14px',
            borderRadius: '8px',
            padding: '12px 16px',
          },
          success: {
            duration: 3000,
            iconTheme: {
              primary: '#10B981',
              secondary: '#fff',
            },
          },
          error: {
            duration: 5000,
            iconTheme: {
              primary: '#EF4444',
              secondary: '#fff',
            },
          },
          loading: {
            iconTheme: {
              primary: '#3B82F6',
              secondary: '#fff',
            },
          },
        }}
      />
    </Router>
    </QueryClientProvider>
  );
}

export default App;
